import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, FormControlLabel, ListItemText, MenuItem,
  Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { API_BASE_URL } from "../config";
import {
  PATIENT_SEXES, SCREENING_STATUSES, SCREENING_STATUS_COLOR, WATCH_LIST_STATUS,
} from "../constants";
import { downloadCsvTemplate } from "../csv";
import { exportEventParticipants } from "../exportExcel";
import { useMe } from "../MeContext";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { useWriteLock } from "../useWriteLock";

const PARTICIPANTS_API = `${API_BASE_URL}/participants`;

const BLANK = {
  name: "", race: "", race_number: "", age: "", date_of_birth: "", sex: "",
  phone: "", email: "", medical_information: "", screening_status: SCREENING_STATUSES[0],
  notes: "", watch_list_notes: "",
  emergency_contact_name: "", emergency_contact_phone: "",
};

// Canonical CSV column names (recognised by the backend).
const CSV_HEADERS = [
  "Name", "Race", "Race number", "Age", "Date of birth", "Sex", "Phone", "Email",
  "Medical information", "Screening status", "Notes", "Watch list notes",
  "Emergency contact name", "Emergency contact phone",
];

/**
 * One event's entry list — the competitors, bulk-loaded from a CSV and referenced
 * by incidents as the patient. Saved instantly: this dialog has no Save button of
 * its own, each participant being added or edited in the dialog it opens.
 *
 * Props:
 *  - event:   the event record ({id, label_long, …}); null when closed
 *  - open:    boolean
 *  - onClose: () => void
 */
export default function EventParticipantsDialog({ event, open, onClose }) {
  const { can } = useMe();
  const fullScreen = useFullScreenDialog();
  const eventId = event?.id;

  const [participants, setParticipants] = useState([]);
  const [editing, setEditing] = useState(null);   // null | "new" | id
  const [form, setForm] = useState(BLANK);
  const [initial, setInitial] = useState(BLANK);  // form snapshot at open, to detect unsaved changes
  const [confirmClose, setConfirmClose] = useState(false); // discard-unsaved-changes prompt
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // {created, skipped, columns} | {error}
  const [filter, setFilter] = useState("");          // by name / race number
  const [medFilter, setMedFilter] = useState(false); // only those with medical information
  const [screenFilter, setScreenFilter] = useState([]); // screening statuses to show ([] = any)
  // Save posts a new entrant, so a double-tap would add two (see useWriteLock).
  const { busy: saving, run: runWrite } = useWriteLock();

  const refresh = () =>
    axios.get(PARTICIPANTS_API + "/", { params: { event_id: eventId } })
      .then(res => setParticipants(res.data));

  useEffect(() => {
    if (!open || !eventId) return;
    setEditing(null);
    setUploadResult(null);
    setConfirmDeleteAll(false);
    setFilter(""); setMedFilter(false); setScreenFilter([]);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventId]);

  const perms = {
    add: can("EventParticipants.add", { eventId }), edit: can("EventParticipants.edit", { eventId }),
    del: can("EventParticipants.delete", { eventId }), deleteAll: can("EventParticipants.deleteAll", { eventId }),
    upload: can("EventParticipants.uploadCSV", { eventId }),
    export: can("EventParticipants.export", { eventId }),
  };

  const openNew = () => { setForm(BLANK); setInitial(BLANK); setEditing("new"); };
  const openEdit = (p) => {
    const loaded = {
      name: p.name ?? "", race: p.race ?? "", race_number: p.race_number ?? "",
      age: p.age ?? "", date_of_birth: p.date_of_birth ?? "",
      sex: p.sex ?? "", phone: p.phone ?? "", email: p.email ?? "",
      medical_information: p.medical_information ?? "",
      screening_status: p.screening_status || SCREENING_STATUSES[0],
      notes: p.notes ?? "",
      watch_list_notes: p.watch_list_notes ?? "",
      emergency_contact_name: p.emergency_contact_name ?? "",
      emergency_contact_phone: p.emergency_contact_phone ?? "",
    };
    setForm(loaded);
    setInitial(loaded);
    setEditing(p.id);
  };
  const closeEdit = () => { setEditing(null); setConfirmClose(false); };
  // Closing (backdrop, Escape or Cancel) warns first if there are unsaved changes.
  const isDirty = () => JSON.stringify(form) !== JSON.stringify(initial);
  const requestCloseEdit = () => (isDirty() ? setConfirmClose(true) : closeEdit());
  const field = (key) => ({
    value: form[key],
    onChange: e => setForm({ ...form, [key]: e.target.value }),
  });
  const payload = () => ({
    name: form.name || null,
    race: form.race || null,
    race_number: form.race_number || null,
    age: form.age === "" ? null : Number(form.age),
    date_of_birth: form.date_of_birth || null,
    sex: form.sex || null,
    phone: form.phone || null,
    email: form.email || null,
    medical_information: form.medical_information || null,
    // Never null — clearing the field means "Not Required", not "unknown".
    screening_status: form.screening_status || SCREENING_STATUSES[0],
    notes: form.notes || null,
    // Sent whatever the status is: the field is hidden once the status moves off
    // the watch list, but hiding it must not delete what someone wrote.
    watch_list_notes: form.watch_list_notes || null,
    emergency_contact_name: form.emergency_contact_name || null,
    emergency_contact_phone: form.emergency_contact_phone || null,
  });
  const save = () => runWrite(() => {
    const after = () => { refresh(); closeEdit(); };
    return editing === "new"
      ? axios.post(PARTICIPANTS_API + "/", { event_id: eventId, ...payload() }).then(after)
      : axios.put(`${PARTICIPANTS_API}/${editing}`, payload()).then(after);
  });
  const remove = () =>
    axios.delete(`${PARTICIPANTS_API}/${editing}`).then(() => { refresh(); closeEdit(); });

  const uploadCsv = (file) => {
    if (!file) return;
    setUploadResult(null);
    const data = new FormData();
    data.append("event_id", eventId);
    data.append("file", file);
    axios.post(`${PARTICIPANTS_API}/upload`, data)
      .then(res => { setUploadResult(res.data); refresh(); })
      .catch(err => setUploadResult({ error: err.response?.data?.detail || "Upload failed." }));
  };

  const deleteAll = () =>
    axios.delete(PARTICIPANTS_API + "/", { params: { event_id: eventId } })
      .then(() => { setConfirmDeleteAll(false); setUploadResult(null); refresh(); });

  // The entry list as narrowed by the three filters — also what the Excel export
  // writes, so the sheet always matches what is on screen.
  const shown = participants.filter(p => {
    const f = filter.trim().toLowerCase();
    if (f && !(p.name || "").toLowerCase().includes(f) &&
        !(p.race_number || "").toLowerCase().includes(f)) return false;
    if (medFilter && !(p.medical_information || "").trim()) return false;
    if (screenFilter.length &&
        !screenFilter.includes(p.screening_status || SCREENING_STATUSES[0])) return false;
    return true;
  });

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>Participants ({participants.length}) — {event?.label_long || event?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {perms.add && (
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openNew}>
                  Add participant
                </Button>
              )}
              {perms.upload && (
                <Button size="small" variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                  Upload CSV
                  <input type="file" accept=".csv,text/csv" hidden
                         onChange={e => { uploadCsv(e.target.files[0]); e.target.value = ""; }} />
                </Button>
              )}
              {perms.upload && (
                <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />}
                        onClick={() => downloadCsvTemplate(CSV_HEADERS, "participants-template.csv")}>
                  CSV template
                </Button>
              )}
              {perms.export && (
                <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />}
                        disabled={shown.length === 0}
                        onClick={() => exportEventParticipants(event, shown)}>
                  Export to Excel
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              {perms.deleteAll && (
                <Button size="small" color="error" startIcon={<DeleteSweepIcon />}
                        disabled={participants.length === 0}
                        onClick={() => setConfirmDeleteAll(true)}>
                  Delete all
                </Button>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              CSV header row with any of: Name, Race, Race number, Age, Date of birth, Sex, Phone,
              Email, Medical information, Screening status, Notes, Watch list notes,
              Emergency contact name, Emergency contact phone.
            </Typography>
            {uploadResult && (uploadResult.error ? (
              <Alert severity="error" onClose={() => setUploadResult(null)}>{uploadResult.error}</Alert>
            ) : (
              <Alert severity="success" onClose={() => setUploadResult(null)}>
                Imported {uploadResult.created} participant{uploadResult.created === 1 ? "" : "s"}
                {uploadResult.skipped ? `, skipped ${uploadResult.skipped} (no name or race number)` : ""}.
              </Alert>
            ))}
            {participants.length > 0 ? (
              <>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
                  <TextField size="small" label="Filter by name or race number" value={filter}
                             onChange={e => setFilter(e.target.value)}
                             helperText={`${shown.length} of ${participants.length}`} />
                  <TextField
                    label="Screening status" select size="small" sx={{ minWidth: 200 }}
                    value={screenFilter}
                    onChange={e => setScreenFilter(
                      typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
                    slotProps={{
                      inputLabel: { shrink: true },
                      select: {
                        multiple: true,
                        displayEmpty: true,
                        renderValue: (selected) => (
                          selected.length === 0
                            ? <Typography variant="body2" color="text.secondary">Any</Typography>
                            : (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {selected.map(v => <Chip key={v} label={v} size="small" />)}
                              </Box>
                            )
                        ),
                      },
                    }}
                  >
                    {SCREENING_STATUSES.map(s => (
                      <MenuItem key={s} value={s}>
                        <Checkbox checked={screenFilter.indexOf(s) > -1} />
                        <ListItemText primary={s} />
                      </MenuItem>
                    ))}
                  </TextField>
                  <FormControlLabel
                    label="Has medical information"
                    control={<Checkbox size="small" checked={medFilter}
                                       onChange={e => setMedFilter(e.target.checked)} />} />
                </Box>
                <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                        <TableCell>Name</TableCell>
                        <TableCell>Race</TableCell>
                        <TableCell>Race no.</TableCell>
                        <TableCell>Screening</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shown.map(p => {
                        const screening = p.screening_status || SCREENING_STATUSES[0];
                        return (
                          <TableRow hover key={p.id} onClick={() => openEdit(p)} sx={{ cursor: "pointer" }}>
                            <TableCell>{p.name || "—"}</TableCell>
                            <TableCell>{p.race || "—"}</TableCell>
                            <TableCell>{p.race_number || "—"}</TableCell>
                            <TableCell>
                              <Chip size="small" label={screening}
                                    color={SCREENING_STATUS_COLOR[screening] || "default"} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {shown.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ color: "text.secondary", py: 2 }}>
                            No participants match your filters.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No entry list yet — upload one as a CSV, or add competitors one at a time.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Participant add / edit dialog */}
      <Dialog open={editing !== null} onClose={requestCloseEdit} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>{editing === "new" ? "Add participant" : (form.name || "Edit participant")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" fullWidth {...field("name")} />
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField label="Race" sx={{ flex: 1, minWidth: 160 }}
                         helperText='e.g. "100 mile", "50 km"' {...field("race")} />
              <TextField label="Race number" sx={{ flex: 1, minWidth: 140 }} {...field("race_number")} />
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField label="Age" type="number" sx={{ width: 120 }}
                         slotProps={{ htmlInput: { min: 0, step: 1 } }} {...field("age")} />
              <TextField label="Date of birth" type="date" sx={{ flex: 1, minWidth: 180 }}
                         slotProps={{ inputLabel: { shrink: true } }} {...field("date_of_birth")} />
              <TextField label="Sex" select sx={{ flex: 1, minWidth: 200 }} {...field("sex")}>
                <MenuItem value=""><em>—</em></MenuItem>
                {PATIENT_SEXES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField label="Phone" sx={{ flex: 1, minWidth: 200 }} {...field("phone")} />
              <TextField label="Email" type="email" sx={{ flex: 1, minWidth: 200 }}
                         {...field("email")} />
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField label="Emergency contact name" sx={{ flex: 1, minWidth: 200 }}
                         {...field("emergency_contact_name")} />
              <TextField label="Emergency contact phone" sx={{ flex: 1, minWidth: 200 }}
                         {...field("emergency_contact_phone")} />
            </Box>
            <TextField label="Medical information" fullWidth multiline minRows={3}
                       helperText="Anything declared that a responder should know. Line breaks are kept."
                       {...field("medical_information")} />
            <TextField label="Screening status" select sx={{ maxWidth: 240 }}
                       {...field("screening_status")}>
              {SCREENING_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField label="Notes" fullWidth multiline minRows={3}
                       helperText="Our own notes on this entrant (screening progress, chasing, …)."
                       {...field("notes")} />
            {/* Only meaningful on the watch list, so only shown there. Any text
                already saved is kept if the status later moves off it. */}
            {form.screening_status === WATCH_LIST_STATUS && (
              <TextField label="Watch list notes" fullWidth multiline minRows={3}
                         helperText="Why they are on the watch list and what to watch for."
                         {...field("watch_list_notes")} />
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
              {editing !== "new" && perms.del && (
                <Button color="error" startIcon={<DeleteIcon />} onClick={remove}>Delete</Button>
              )}
              <Box sx={{ flex: 1 }} />
              <Button onClick={requestCloseEdit}>Cancel</Button>
              {(editing === "new" ? perms.add : perms.edit) && (
                <Button variant="contained" onClick={save} disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} /> : undefined}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              )}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Participant unsaved-changes confirmation */}
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes to this participant. If you close now, they will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={closeEdit}>Discard changes</Button>
        </DialogActions>
      </Dialog>

      {/* Delete-all confirmation */}
      <Dialog open={confirmDeleteAll} onClose={() => setConfirmDeleteAll(false)}>
        <DialogTitle>Delete all participants?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete all {participants.length} participant
            {participants.length === 1 ? "" : "s"} for {event?.name}. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteAll(false)}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteSweepIcon />} onClick={deleteAll}>
            Delete all
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
